import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import AkFlag, { FlagGroup } from '@atlaskit/flag';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';

import { dismissSubtypeAdjustedFlag } from '../../editor-commands/subtype-adjusted-flag';
import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { messages } from '../messages';

type Props = {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
};

/**
 * Renders the "Output adjusted" flag (backend produced a different type than
 * selected). Driven by durable plugin state so it survives the remix content
 * component tearing down on completion.
 */
export const SubtypeAdjustedFlag = ({ api }: Props): React.JSX.Element | null => {
	const intl = useIntl();
	const isShown = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.showSubtypeAdjustedFlag ?? false,
	);

	const onDismissed = useCallback(() => {
		api?.core.actions.execute(dismissSubtypeAdjustedFlag());
	}, [api]);

	if (!isShown) {
		return null;
	}

	return (
		<FlagGroup>
			<AkFlag
				onDismissed={onDismissed}
				title={intl.formatMessage(messages.subtypeAdjustedFlagTitle)}
				description={intl.formatMessage(messages.subtypeAdjustedFlagDescription)}
				id="maui-remix-subtype-adjusted"
				testId="maui-remix-subtype-adjusted"
				icon={<StatusInformationIcon label="" color={token('color.icon.information')} />}
			/>
		</FlagGroup>
	);
};
